// Модель редактора шаблона: строки ввода ↔ TemplateInput.

import { parseDecimal, pluralRu } from '@/lib/format'
import type { EditItem, EditOperation } from '@/pages/ai/api'
import type { Template, TemplateInput, Tracking } from '@/pages/workouts/api'
import { newId, parseDuration, summarizeSets } from '@/pages/workouts/logic'

export type TargetRow = { key: string; reps: string; weight: string; duration: string; distance: string; rest: string }

export type TemplateItem = {
  key: string
  id: string | null
  exercise_id: string
  name: string
  tracking: Tracking
  archived: boolean
  rows: TargetRow[]
  progression: boolean
  increment: string
  notes: string
}

export type TemplateForm = { name: string; notes: string; items: TemplateItem[] }

export const DEFAULT_REST_S = 90

const text = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v).replace('.', ','))

const num = (s: string) => (s.trim() ? parseDecimal(s) : null)

const durationText = (s: number | null | undefined) =>
  s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : ''

export function emptyRow(prev?: TargetRow): TargetRow {
  return prev ? { ...prev, key: newId() } : { key: newId(), reps: '', weight: '', duration: '', distance: '', rest: String(DEFAULT_REST_S) }
}

export function newItem(exercise: { id: string; name: string; tracking: Tracking }): TemplateItem {
  return {
    key: newId(),
    id: null,
    exercise_id: exercise.id,
    name: exercise.name,
    tracking: exercise.tracking,
    archived: false,
    rows: [emptyRow(), emptyRow(), emptyRow()],
    progression: exercise.tracking === 'weight_reps',
    increment: '2,5',
    notes: '',
  }
}

/**
 * Заменить упражнение на месте: остаются подходы, отдых, прогрессия и заметка. Вес у другого
 * упражнения свой — он очищается; другой способ учёта — очищаются и повторы, время, дистанция.
 */
export function replaceItem(item: TemplateItem, exercise: { id: string; name: string; tracking: Tracking }): TemplateItem {
  const same = item.tracking === exercise.tracking
  const fresh = newItem(exercise)
  return {
    ...item,
    id: null,
    exercise_id: exercise.id,
    name: exercise.name,
    tracking: exercise.tracking,
    archived: false,
    rows: (item.rows.length ? item.rows : fresh.rows).map((row) => ({
      ...(same ? row : emptyRow()),
      key: row.key,
      weight: '',
      rest: row.rest,
    })),
    progression: same ? item.progression : fresh.progression,
  }
}

export function formFromTemplate(template: Template | null): TemplateForm {
  if (!template) return { name: '', notes: '', items: [] }
  return {
    name: template.name,
    notes: template.notes ?? '',
    items: template.exercises.map((e) => ({
      key: e.id,
      id: e.id,
      exercise_id: e.exercise_id,
      name: e.exercise_name,
      tracking: e.tracking,
      archived: e.exercise_archived,
      rows: e.target_sets.map((s) => ({
        key: newId(),
        reps: text(s.reps),
        weight: text(s.weight_kg),
        duration: durationText(s.duration_s),
        distance: text(s.distance_m),
        rest: text(s.rest_s),
      })),
      progression: e.progression.enabled,
      increment: text(e.progression.increment_kg),
      notes: e.notes ?? '',
    })),
  }
}

/** Форма → тело запроса; ошибка — строка для человека. */
export function formToInput(form: TemplateForm): { input: TemplateInput | null; error: string | null } {
  if (!form.name.trim()) return { input: null, error: 'Укажи название шаблона' }
  const exercises: TemplateInput['exercises'] = []
  for (const item of form.items) {
    const sets = []
    for (const row of item.rows) {
      const values = { reps: num(row.reps), weight: num(row.weight), distance: num(row.distance), rest: num(row.rest) }
      const bad = (Object.keys(values) as (keyof typeof values)[]).some(
        (k) => values[k] === null && row[k].trim() !== '',
      )
      if (bad) return { input: null, error: `Проверь числа в упражнении «${item.name}»` }
      sets.push({
        reps: values.reps === null ? null : Math.round(values.reps),
        weight_kg: values.weight,
        duration_s: parseDuration(row.duration),
        distance_m: values.distance,
        rest_s: values.rest === null ? null : Math.round(values.rest),
      })
    }
    const increment = item.increment.trim() ? parseDecimal(item.increment) : 0
    if (increment === null || increment < 0) return { input: null, error: `Шаг прогрессии в «${item.name}» — число в кг` }
    exercises.push({
      id: item.id,
      exercise_id: item.exercise_id,
      target_sets: sets,
      progression: { enabled: item.progression, increment_kg: increment },
      notes: item.notes.trim() || null,
    })
  }
  return { input: { name: form.name.trim(), notes: form.notes.trim() || null, exercises }, error: null }
}

/** Сводка подходов для свёрнутого упражнения: «3 × 8 × 80 кг»; без цифр — «3 подхода». */
export function itemSummary(item: TemplateItem): string {
  if (item.rows.length === 0) return 'нет подходов'
  const sets = item.rows.map((row) => ({
    reps: num(row.reps),
    weight_kg: num(row.weight),
    duration_s: parseDuration(row.duration),
    distance_m: num(row.distance),
  }))
  const empty = sets.every((s) => s.reps === null && s.weight_kg === null && s.duration_s === null && s.distance_m === null)
  if (empty) return `${sets.length} ${pluralRu(sets.length, ['подход', 'подхода', 'подходов'])}`
  return summarizeSets(sets, item.tracking)
}

/** Упражнения формы — для правки с ИИ (числа, которые не разобрать, — пусто). */
export function toEditItems(items: TemplateItem[]): EditItem[] {
  return items.map((item) => ({
    key: item.key,
    exercise_id: item.exercise_id,
    name: item.name,
    tracking: item.tracking,
    sets: item.rows.map((row) => ({
      reps: num(row.reps),
      weight_kg: num(row.weight),
      duration_s: parseDuration(row.duration),
      distance_m: num(row.distance),
      rest_s: num(row.rest),
    })),
  }))
}

/** Одинаковые строки подходов по цели от ИИ; без отдыха — 90 с. */
function rowsFrom(target: {
  sets: number
  reps: number | null
  weight_kg: number | null
  duration_s: number | null
  distance_m: number | null
  rest_s: number | null
}): TargetRow[] {
  return Array.from({ length: target.sets }, () => ({
    key: newId(),
    reps: text(target.reps),
    weight: text(target.weight_kg),
    duration: durationText(target.duration_s),
    distance: text(target.distance_m),
    rest: text(target.rest_s ?? DEFAULT_REST_S),
  }))
}

/**
 * Применить правки ИИ к форме. Несколько вставок «после X» идут по порядку ответа; правки
 * упражнений, которых уже нет, и упражнения не из справочника пропускаются.
 */
export function applyEdit(items: TemplateItem[], operations: EditOperation[]): TemplateItem[] {
  let list = [...items]
  // Куда вставлять следующее «после X»: после последнего вставленного туда же.
  const anchors = new Map<string, string>()
  const insert = (item: TemplateItem, after: string | null) => {
    if (after === null) {
      list.push(item)
      return
    }
    const anchor = anchors.get(after) ?? (after === 'start' ? null : after)
    const index = anchor === null ? -1 : list.findIndex((it) => it.key === anchor)
    if (anchor !== null && index < 0) list.push(item)
    else list.splice(index + 1, 0, item)
    anchors.set(after, item.key)
  }
  for (const op of operations) {
    const index = op.key === null ? -1 : list.findIndex((it) => it.key === op.key)
    const ex = op.exercise
    if (op.op === 'add') {
      if (!ex?.exercise_id) continue
      const item = newItem({ id: ex.exercise_id, name: ex.exercise_name ?? ex.name, tracking: ex.tracking as Tracking })
      insert({ ...item, rows: rowsFrom(ex) }, op.after)
      continue
    }
    if (index < 0) continue
    const current = list[index]!
    if (op.op === 'remove') {
      list = list.filter((_, i) => i !== index)
    } else if (op.op === 'replace') {
      if (!ex?.exercise_id) continue
      const item = newItem({ id: ex.exercise_id, name: ex.exercise_name ?? ex.name, tracking: ex.tracking as Tracking })
      list[index] = { ...item, rows: rowsFrom(ex) }
    } else if (op.op === 'update') {
      let rows = current.rows
      if (op.sets !== null) {
        rows = rows.slice(0, op.sets)
        while (rows.length < op.sets) rows = [...rows, emptyRow(rows.at(-1))]
      }
      rows = rows.map((row) => ({
        ...row,
        reps: op.reps !== null ? text(op.reps) : row.reps,
        weight: op.weight_kg !== null ? text(op.weight_kg) : row.weight,
        duration: op.duration_s !== null ? durationText(op.duration_s) : row.duration,
        distance: op.distance_m !== null ? text(op.distance_m) : row.distance,
        rest: op.rest_s !== null ? text(op.rest_s) : row.rest,
      }))
      list[index] = { ...current, rows }
    } else {
      list = list.filter((_, i) => i !== index)
      insert(current, op.after)
    }
  }
  return list
}

export function moveItem<T>(list: T[], index: number, delta: number): T[] {
  const target = index + delta
  if (target < 0 || target >= list.length) return list
  const next = [...list]
  const [item] = next.splice(index, 1)
  next.splice(target, 0, item!)
  return next
}
